import { useCallback } from 'react';
import { BadgeIcon, Button, Checkbox, Dialog, Group, Tooltip } from '@economic/taco';
import { t, Trans } from '@lingui/macro';
import { useField } from 'react-final-form';
import { Field } from '~/app/shared/components/Form';
import ExternalLink from '~/app/shared/components/ExternalLink';
import { CountryDropdown } from '~/app/shared/components/ui/dropdowns/CountryDropdown/CountryDropdown';
import EditCompanyInfoDialog from '~/app/pages/settings/master-information/dialogs/EditCompanyInfoDialog';
import { isCvrReadonly } from '~/app/pages/settings/master-information/sections/CompanyInfo';
import { Plan, useCurrentAgreement, useCurrentUser } from '@economic/react-app';
import { CompanyResponse, CompanyResponseModel } from '~/payment-cards-feature';
import { validateCompanyCvr, validateCompanyName } from '../../utils';

const validateOnlySelf: [] = [];

interface Props {
    prevStep: () => void;
    nextStep: () => void;
    companyData: CompanyResponse;
}

const CompanyInformation = ({ prevStep, nextStep, companyData }: Props) => {
    const fieldCompanyCvr = useField('companyCvr');
    const fieldCompanyName = useField('companyName');
    const fieldPhysicalProduct = useField('businessType.physicalProduct');
    const fieldProductOneName = useField('physicalProductDetail.one.name');
    const fieldProductOnePrice = useField('physicalProductDetail.one.averagePrice');
    const fieldProductTwoName = useField('physicalProductDetail.two.name');
    const fieldProductTwoPrice = useField('physicalProductDetail.two.averagePrice');
    const fieldProductThreeName = useField('physicalProductDetail.three.name');
    const fieldProductThreePrice = useField('physicalProductDetail.three.averagePrice');
    const fieldEstimatedMaxTransactionAmount = useField('physicalProductDetail.estimatedMaxTransactionAmount');
    const fieldMarkets = useField('physicalProductDetail.markets');
    const fieldCompanyDescription = useField('companyDescription');
    const isPhysicalProductChecked = fieldPhysicalProduct.input.value;

    const user = useCurrentUser();
    const { number: agreementNumber, subscription, cvr } = useCurrentAgreement();
    const isAdministrator = user.isAdministrator && agreementNumber === user.agreement.number;
    const isInternalAgreement = subscription.level === Plan.Internal;

    const onPrevClick = () => {
        prevStep();
    };

    const onNextClick = () => {
        fieldCompanyDescription.input.onBlur();
        fieldCompanyCvr.input.onBlur();
        fieldCompanyName.input.onBlur();

        if (isPhysicalProductChecked) {
            fieldProductOneName.input.onBlur();
            fieldProductOnePrice.input.onBlur();

            fieldProductTwoName.input.onBlur();
            fieldProductTwoPrice.input.onBlur();

            fieldProductThreeName.input.onBlur();
            fieldProductThreePrice.input.onBlur();

            fieldEstimatedMaxTransactionAmount.input.onBlur();
            fieldMarkets.input.onBlur();
        }

        if (fieldCompanyDescription.meta.invalid || fieldCompanyCvr.meta.invalid || fieldCompanyName.meta.invalid) {
            return;
        }

        if (isPhysicalProductChecked) {
            if (
                fieldProductOneName.meta.invalid ||
                fieldProductOnePrice.meta.invalid ||
                fieldProductTwoName.meta.invalid ||
                fieldProductTwoPrice.meta.invalid ||
                fieldProductThreeName.meta.invalid ||
                fieldProductThreePrice.meta.invalid ||
                fieldEstimatedMaxTransactionAmount.meta.invalid ||
                fieldMarkets.meta.invalid
            ) {
                return;
            }
        }

        nextStep();
    };

    // Error if description doesn't contain 5 or more words
    const validateCompanyDescription = useCallback((value: string) => {
        return value.replaceAll(' ', '').length >= 25 ? undefined : t`Describe what your company sells in at least 25 characters`;
    }, []);

    // Error if no value
    const validateRequiredField = useCallback((value: string | number) => {
        return value ? undefined : t`Field is required`;
    }, []);

    // Error array is empty
    const validateCountries = useCallback((value: string[]) => {
        return value && value.length > 0 ? undefined : t`Select your markets`;
    }, []);

    return (
        <>
            <Dialog.Title>
                <Trans>Verify the company information</Trans>
            </Dialog.Title>

            <div className="flex mt-3">
                <Field.Text
                    required
                    fieldClassName="w-full"
                    label={t`CVR number`}
                    name="companyCvr"
                    validate={validateCompanyCvr}
                    readOnly
                />
            </div>

            <div className="flex mt-1">
                <Field.Text
                    required
                    fieldClassName="w-full"
                    label={t`Company name`}
                    name="companyName"
                    validate={validateCompanyName}
                    readOnly
                />
            </div>

            <div className="flex justify-end">
                <Button
                    appearance="discrete"
                    dialog={dialogProps => (
                        <EditCompanyInfoDialog
                            {...dialogProps}
                            initialData={companyData?.model}
                            onSubmitted={({ cvr, name }: CompanyResponseModel) => {
                                // Update values
                                fieldCompanyCvr.input.onChange(cvr);
                                fieldCompanyName.input.onChange(name);
                                // Trigger validation
                                fieldCompanyCvr.input.onBlur();
                                fieldCompanyName.input.onBlur();
                            }}
                            isAdministrator={isAdministrator}
                            isCvrReadonly={isCvrReadonly(companyData?.model, cvr, subscription)}
                            isInternalAgreement={isInternalAgreement}
                            isAdministratorPayingForCurrentAgreement={companyData?.isAdministratorPayingForCurrentAgreement}
                            enforce2FAReason={companyData?.enforce2FAReason}
                            shouldDisable2FA={companyData?.shouldDisable2FA}
                        />
                    )}>
                    <Trans>Update master information</Trans>
                </Button>
            </div>

            <div className="flex mt-1">
                <span className="font-bold text-xs">
                    <Trans>What does your company sell?</Trans> *
                </span>
                <Tooltip
                    title={t`In order for pensopay to approve your redemption agreement application, they need to know what you sell in your business`}>
                    <BadgeIcon small icon="info" color="blue" className="ml-1" />
                </Tooltip>
            </div>

            <div className="flex-col mt-2">
                <Group>
                    <label className="mr-1">
                        <Field.Checkbox fieldClassName="flex" name="businessType.physicalProduct">
                            {({ input: { name, value, onChange } }) => (
                                <>
                                    <Checkbox
                                        id="physical-product-checkbox-button"
                                        className="!mt-0 mr-1"
                                        name={name}
                                        checked={value}
                                        onChange={onChange}
                                    />
                                    <Trans>Physical products</Trans>
                                </>
                            )}
                        </Field.Checkbox>
                    </label>
                    <label className="mr-1">
                        <Field.Checkbox fieldClassName="flex" name="businessType.digitalSoftware">
                            {({ input: { name, value, onChange } }) => (
                                <>
                                    <Checkbox
                                        id="digital-software-checkbox-button"
                                        className="!mt-0 mr-1"
                                        name={name}
                                        checked={value}
                                        onChange={onChange}
                                    />
                                    <Trans>Digital software</Trans>
                                </>
                            )}
                        </Field.Checkbox>
                    </label>
                    <label className="mr-1">
                        <Field.Checkbox fieldClassName="flex" name="businessType.services">
                            {({ input: { name, value, onChange } }) => (
                                <>
                                    <Checkbox
                                        id="services-checkbox-button"
                                        className="!mt-0 mr-1"
                                        name={name}
                                        checked={value}
                                        onChange={onChange}
                                    />
                                    <Trans>Services</Trans>
                                </>
                            )}
                        </Field.Checkbox>
                    </label>
                </Group>
            </div>

            <div className={`flex-col mt-4 ${isPhysicalProductChecked ? '' : 'hidden'}`}>
                <Group>
                    <Field.Text
                        required
                        fieldClassName="w-8/12"
                        label={t`Input your 3 most selling physical products`}
                        name="physicalProductDetail.one.name"
                        validate={validateRequiredField}
                        validateFields={validateOnlySelf}
                    />
                    <Field.Number
                        required
                        fieldClassName="w-4/12"
                        label={t`Average price DKK`}
                        name="physicalProductDetail.one.averagePrice"
                        validate={validateRequiredField}
                        validateFields={validateOnlySelf}
                        decimal
                    />
                </Group>
                <Group>
                    <Field.Text
                        required
                        fieldClassName="w-8/12 h-12"
                        name="physicalProductDetail.two.name"
                        validate={validateRequiredField}
                        validateFields={validateOnlySelf}
                    />
                    <Field.Number
                        required
                        fieldClassName="w-4/12 h-12"
                        name="physicalProductDetail.two.averagePrice"
                        validate={validateRequiredField}
                        validateFields={validateOnlySelf}
                        decimal
                    />
                </Group>
                <Group>
                    <Field.Text
                        required
                        fieldClassName="w-8/12 h-12"
                        name="physicalProductDetail.three.name"
                        validate={validateRequiredField}
                        validateFields={validateOnlySelf}
                    />
                    <Field.Number
                        required
                        fieldClassName="w-4/12 h-12"
                        name="physicalProductDetail.three.averagePrice"
                        validate={validateRequiredField}
                        validateFields={validateOnlySelf}
                        decimal
                    />
                </Group>
                <Group>
                    <Field.Number
                        required
                        fieldClassName="w-8/12"
                        label={t`Estimated max transaction amount DKK`}
                        name="physicalProductDetail.estimatedMaxTransactionAmount"
                        validate={validateRequiredField}
                        validateFields={validateOnlySelf}
                        decimal
                    />

                    <Field
                        required
                        fieldClassName="w-4/12"
                        name="physicalProductDetail.markets"
                        validate={validateCountries}
                        validateFields={validateOnlySelf}>
                        {({ input, meta: { error, touched } }) => (
                            <label className="flex flex-col font-bold text-xs leading-loose pb-4 min-h-[theme(spacing.18)]">
                                <span className="flex items-center">
                                    <Trans>Markets</Trans> *
                                    <Tooltip title={t`Select the countries you sell to`}>
                                        <BadgeIcon small icon="info" color="blue" className="ml-1" />
                                    </Tooltip>
                                </span>
                                <CountryDropdown multiple invalid={error && touched} {...input} />
                                {error && touched && (
                                    <span className="h-4 text-xs text-left leading-normal font-normal truncate -mb-4 text-red-500">
                                        {error}
                                    </span>
                                )}
                            </label>
                        )}
                    </Field>
                </Group>
            </div>

            <div className="mt-2">
                <Field.TextArea
                    required
                    placeholder={t`Example: I sell consulting services within bookkeeping. I help my clients manage their accounting by providing help and counseling`}
                    label={t`Describe your business model`}
                    name="companyDescription"
                    validate={validateCompanyDescription}
                    validateFields={validateOnlySelf}
                    rows={3}
                />
            </div>

            <div className="flex mt-2">
                <span>
                    <Trans>
                        Some business types cannot be approved by pensopay - e.g., gambling, weapons, the sale of tobacco, and
                        alcohol. See the full{' '}
                    </Trans>{' '}
                    <ExternalLink href="https://www.e-conomic.dk/support/artikler/opsaetning-af-betalingslink#section1">
                        <Trans>list</Trans>
                    </ExternalLink>
                    .
                </span>
            </div>

            <Group className="flex mt-6 justify-end">
                <Button onClick={onPrevClick}>
                    <Trans>Back</Trans>
                </Button>
                <Button appearance="primary" onClick={onNextClick}>
                    <Trans>Next</Trans>
                </Button>
            </Group>
        </>
    );
};

export default CompanyInformation;
